import type { MarketingRouterRule } from '~/types/marketingRouterRule';

import { MATCH_FIELD_LABELS, toMatchValues } from './marketingRouterMatch';

type Rule = MarketingRouterRule;
type Variant = Rule['variants'][number];

const METRIC_LABELS: Record<string, string> = { ttv: 'TTV', margin: 'Margin', purchase: 'Conversion' };

function formatValues(values: Array<string>): string {
  return values.length ? values.join(', ') : 'any';
}

function formatMetrics(metrics: Array<string>): string {
  // Comma-joined in order: the arrow is reserved for before → after.
  return metrics.length ? metrics.map((metric) => METRIC_LABELS[metric] ?? metric).join(', ') : 'none';
}

function countTreatments(variant: Variant | undefined): number {
  return variant?.treatments?.length ?? 0;
}

function describeTreatments(variant: Variant | undefined): string {
  const treatments = variant?.treatments ?? [];
  return treatments.length ? treatments.map((treatment) => treatment.type).join(', ') : 'none';
}

function getControlName(rule: Rule): string {
  const control = rule.variants.find((variant) => variant.is_control);
  return control?.variant_name?.trim() || 'none';
}

/**
 * What differs between the saved rule and the one on screen, one line per
 * change, in the order the form presents them.
 *
 * Both sides must have been through `prepareRule`, so trimming and date
 * normalisation never read as an edit. Variants are matched by position: the
 * form has no way to reorder them, so position is identity here.
 */
export function getRuleChanges(saved: Rule | null, current: Rule): Array<string> {
  if (saved == null) {
    return [];
  }

  const changes: Array<string> = [];

  if (saved.name !== current.name) {
    changes.push(`Rule name: “${saved.name}” → “${current.name}”`);
  }
  if (saved.start_date !== current.start_date) {
    changes.push(`Start date: ${saved.start_date} → ${current.start_date}`);
  }
  if (saved.end_date !== current.end_date) {
    changes.push(`End date: ${saved.end_date} → ${current.end_date}`);
  }

  for (const [field, label] of MATCH_FIELD_LABELS) {
    const before = toMatchValues(saved.match[field]);
    const after = toMatchValues(current.match[field]);
    if (before.join(' ') !== after.join(' ')) {
      changes.push(`${label}: ${formatValues(before)} → ${formatValues(after)}`);
    }
  }

  if (saved.significance_metrics.join(',') !== current.significance_metrics.join(',')) {
    changes.push(
      `Metrics: ${formatMetrics(saved.significance_metrics)} → ${formatMetrics(current.significance_metrics)}`,
    );
  }

  if (saved.variants.length !== current.variants.length) {
    changes.push(`Variants: ${saved.variants.length} → ${current.variants.length}`);
  }
  if (getControlName(saved) !== getControlName(current)) {
    changes.push(`Control: ${getControlName(saved)} → ${getControlName(current)}`);
  }

  const variantCount = Math.min(saved.variants.length, current.variants.length);
  for (let index = 0; index < variantCount; index += 1) {
    const before = saved.variants[index];
    const after = current.variants[index];
    const name = after.variant_name?.trim() || `Variant ${index + 1}`;

    if (before.variant_name !== after.variant_name) {
      changes.push(`Variant ${index + 1} name: “${before.variant_name}” → “${after.variant_name}”`);
    }
    if (before.weight !== after.weight) {
      changes.push(`${name} weight: ${before.weight}% → ${after.weight}%`);
    }
    if (countTreatments(before) !== countTreatments(after)) {
      changes.push(`${name} treatments: ${describeTreatments(before)} → ${describeTreatments(after)}`);
    } else if (JSON.stringify(before.treatments ?? []) !== JSON.stringify(after.treatments ?? [])) {
      changes.push(`${name}: treatment settings edited`);
    }
  }

  return changes;
}
